import React from 'react';
import { TrendingUp, Car, DollarSign, Activity } from 'lucide-react';

const PortfolioDashboard = ({ investments, vehicles, payouts, loading }) => {
  const formatCurrency = (val) =>
    new Intl.NumberFormat('en-KE', { style: 'currency', currency: 'KES', minimumFractionDigits: 0 })?.format(val || 0);

  const totalInvested = investments?.reduce((sum, inv) => sum + Number(inv?.amount || 0), 0) || 0;
  const totalEarned = investments?.reduce((sum, inv) => sum + Number(inv?.total_earned || 0), 0) || 0;
  const activeCount = investments?.filter((inv) => inv?.status === 'active')?.length || 0;
  const totalVehicles = investments?.reduce((sum, inv) => sum + Number(inv?.vehicle_count || 0), 0) || 0;

  const tierColors = {
    starter: 'bg-slate-100 text-slate-700',
    growth: 'bg-amber-100 text-amber-800',
    enterprise: 'bg-yellow-100 text-yellow-800',
  };

  const statusColors = {
    active: 'bg-green-100 text-green-700',
    pending: 'bg-blue-100 text-blue-700',
    completed: 'bg-slate-100 text-slate-600',
    cancelled: 'bg-red-100 text-red-600',
  };

  const stats = [
    { label: 'Total Invested', value: formatCurrency(totalInvested), icon: DollarSign, color: 'bg-[#1a2744]', textColor: 'text-white' },
    { label: 'Total Earnings', value: formatCurrency(totalEarned), icon: TrendingUp, color: 'bg-[#c9a84c]', textColor: 'text-[#1a2744]' },
    { label: 'Active Investments', value: activeCount, icon: Activity, color: 'bg-white', textColor: 'text-[#1a2744]', border: true },
    { label: 'Fleet Vehicles', value: totalVehicles, icon: Car, color: 'bg-white', textColor: 'text-[#1a2744]', border: true },
  ];

  if (loading) {
    return (
      <section id="portfolio" className="mb-16">
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-[#1a2744] mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
            My Portfolio
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          {[1, 2, 3, 4]?.map((i) => (
            <div key={i} className="h-28 bg-slate-100 rounded-2xl animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  return (
    <section id="portfolio" className="mb-16">
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-[#1a2744] mb-2" style={{ fontFamily: 'DM Sans, sans-serif' }}>
          My Portfolio
        </h2>
        <p className="text-slate-500 text-sm">Your active investments and fleet performance</p>
      </div>
      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        {stats?.map((stat, i) => (
          <div
            key={i}
            className={`rounded-2xl p-5 ${stat?.color} ${stat?.border ? 'border border-slate-100 shadow-sm' : 'shadow-md'}`}
          >
            <div className={`inline-flex p-2 rounded-xl mb-3 ${stat?.border ? 'bg-slate-50' : 'bg-white/20'}`}>
              <stat.icon size={16} className={stat?.border ? 'text-[#1a2744]' : stat?.textColor} />
            </div>
            <div className={`text-2xl font-bold ${stat?.textColor}`} style={{ fontFamily: 'DM Sans, sans-serif' }}>
              {stat?.value}
            </div>
            <div className={`text-xs mt-1 ${stat?.border ? 'text-slate-400' : stat?.textColor + '/70'}`}>
              {stat?.label}
            </div>
          </div>
        ))}
      </div>
      {/* Investments Table */}
      {investments?.length > 0 ? (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
            <h3 className="font-semibold text-[#1a2744] text-sm">Active Investments</h3>
            <span className="text-xs text-slate-400">{investments?.length} investment{investments?.length !== 1 ? 's' : ''}</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50">
                  <th className="text-left text-xs text-slate-400 font-medium px-6 py-3">Tier</th>
                  <th className="text-left text-xs text-slate-400 font-medium px-6 py-3">Amount</th>
                  <th className="text-left text-xs text-slate-400 font-medium px-6 py-3">ROI</th>
                  <th className="text-left text-xs text-slate-400 font-medium px-6 py-3">Vehicles</th>
                  <th className="text-left text-xs text-slate-400 font-medium px-6 py-3">Earned</th>
                  <th className="text-left text-xs text-slate-400 font-medium px-6 py-3">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {investments?.map((inv) => (
                  <tr key={inv?.id} className="hover:bg-slate-50 transition-colors">
                    <td className="px-6 py-4">
                      <span className={`text-xs font-semibold px-2.5 py-1 rounded-full capitalize ${tierColors?.[inv?.tier] || 'bg-slate-100 text-slate-600'}`}>
                        {inv?.tier}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-[#1a2744]">
                      {formatCurrency(inv?.amount)}
                    </td>
                    <td className="px-6 py-4 text-sm text-green-600 font-medium">
                      {inv?.roi_percentage}%
                    </td>
                    <td className="px-6 py-4 text-sm text-slate-600 flex items-center gap-1">
                      <Car size={13} className="text-slate-400" />
                      {inv?.vehicle_count}
                    </td>
                    <td className="px-6 py-4 text-sm font-semibold text-[#c9a84c]">
                      {formatCurrency(inv?.total_earned)}
                    </td>
                    <td className="px-6 py-4">
                      <span className={`text-xs font-medium px-2.5 py-1 rounded-full capitalize ${statusColors?.[inv?.status] || 'bg-slate-100 text-slate-600'}`}>
                        {inv?.status}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-12 text-center">
          <div className="w-16 h-16 bg-slate-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Activity size={24} className="text-slate-400" />
          </div>
          <h3 className="text-[#1a2744] font-semibold mb-2">No Active Investments</h3>
          <p className="text-slate-400 text-sm">Start investing by selecting a tier above</p>
        </div>
      )}
      {/* Linked Vehicles */}
      {vehicles?.length > 0 && (
        <div className="mt-6 bg-white rounded-2xl shadow-sm border border-slate-100 overflow-hidden">
          <div className="px-6 py-4 border-b border-slate-100">
            <h3 className="font-semibold text-[#1a2744] text-sm">Linked Fleet Vehicles</h3>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-0 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
            {vehicles?.slice(0, 6)?.map((v) => (
              <div key={v?.id} className="px-6 py-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-[#1a2744]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Car size={16} className="text-[#1a2744]" />
                </div>
                <div>
                  <div className="text-sm font-semibold text-[#1a2744]">{v?.vehicle_registration}</div>
                  <div className="text-xs text-slate-400">{v?.vehicle_type} · {v?.vehicle_model}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </section>
  );
};

export default PortfolioDashboard;
